import { useParams, Link } from 'react-router-dom';
import { articles, articleContent, categories } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';

const categoryNameMap: Record<string, string> = {
  'gaoxiao-shipin': '搞笑视频',
  'gaoxiao-duanzi': '搞笑段子',
  'biaoqingbao': '表情包',
  'shenpinglun': '神评论',
  'qipa-shijian': '奇葩事件',
  'shadiao-shunjian': '沙雕瞬间',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const content = article ? articleContent[article.slug] : null;

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      '@type': 'Organization',
      name: '搞笑排行榜',
      url: 'https://ggqcmf.cn/',
    },
    publisher: {
      '@type': 'Organization',
      name: '搞笑排行榜',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://ggqcmf.cn/article/${article.slug}`,
    },
  };

  return (
    <div className="min-h-screen pt-16">
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category?.name || article.category, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article>
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {category && (
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs text-primary-600 bg-primary-100 px-3 py-1 rounded-full font-medium hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {category.name}
                </Link>
              )}
              {article.tag && (
                <span className="text-xs bg-accent-500 text-background-50 px-3 py-1 rounded-full font-medium whitespace-nowrap">
                  {article.tag}
                </span>
              )}
              <span className="text-xs text-foreground-400">{article.publishDate}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount} 次阅读
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line"></i>
                {article.commentCount} 条评论
              </span>
            </div>
          </header>

          <div className="mb-8 rounded-xl overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-auto object-cover"
              title={`${article.title} - 搞笑排行榜`}
            />
          </div>

          <div className="prose-custom text-foreground-700 leading-relaxed">
            <p className="text-base md:text-lg mb-8">{article.excerpt}</p>

            {content ? (
              <>
                <p className="text-base mb-6">{content.summary}</p>

                <div className="space-y-6 mb-8">
                  {content.items.map((item) => (
                    <div
                      key={item.rank}
                      className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 font-bold text-lg">
                          {item.rank}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">
                            TOP{item.rank}：{item.title}
                          </h3>
                          <p className="text-sm text-foreground-600 leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <p className="text-base text-foreground-600">{content.conclusion}</p>
              </>
            ) : (
              <div className="text-center py-10">
                <p className="text-foreground-500">文章内容正在整理中，敬请期待...</p>
              </div>
            )}
          </div>
        </article>

        <div className="mt-10 pt-8 border-t border-background-200/70">
          <p className="text-sm text-foreground-400 mb-4">
            声明：本文内容来源于网络收集整理，仅供娱乐参考。如涉及版权问题，请联系我们处理。
          </p>
        </div>

        {relatedArticles.length > 0 && (
          <section className="mt-10 pt-8 border-t border-background-200/70">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
              更多{categories.find((c) => c.slug === article.category)?.name || ''}排行榜
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-300 group cursor-pointer"
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={ra.coverImage}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {ra.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-2 text-xs text-foreground-400">
                      <span>{ra.publishDate}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {ra.viewCount >= 10000 ? `${(ra.viewCount / 10000).toFixed(1)}万` : ra.viewCount}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}